import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  ComposedChart, Bar, Line, Area, AreaChart, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, BarChart, RadialBarChart, RadialBar, PolarAngleAxis, Legend
} from 'recharts';

const API = 'https://api.cpms-srlkevcharge.com';


function generateDemoData(days) {
  const sessions_chart = [];
  const avg_duration_chart = [];
  for (let i = days; i >= 0; i--) {
    const day = new Date();
    day.setDate(day.getDate() - i);
    const key = day.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
    const sessions = Math.max(0, Math.round(8 + Math.sin(i / 3) * 5 + Math.random() * 6));
    sessions_chart.push({ date: key, sessions, energy_kwh: Math.round(sessions * (12 + Math.random() * 8)) });
    avg_duration_chart.push({ date: key, avg_minutes: Math.round(35 + Math.sin(i / 4) * 10 + Math.random() * 8) });
  }

  const growth_chart = [];
  let cumulative = 2;
  for (let i = Math.min(days, 12); i >= 0; i--) {
    cumulative += Math.random() > 0.6 ? 1 : 0;
    const day = new Date();
    day.setDate(day.getDate() - i * (days / 12));
    growth_chart.push({ date: day.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }), stations: cumulative });
  }

  return {
    metrics: {
      total_chargers: 24,
      total_sessions: sessions_chart.reduce((s, d) => s + d.sessions, 0),
      total_energy_kwh: Math.round(sessions_chart.reduce((s, d) => s + d.energy_kwh, 0)),
      availability_percent: 87.5,
      active_chargers: 21,
    },
    trends: {
      total_sessions_change: 12.4,
      total_energy_kwh_change: -6.8,
    },
    status_breakdown: { Available: 14, Charging: 7, Offline: 2, Faulted: 1 },
    sessions_chart,
    avg_duration_chart,
    connector_types: { 'Type 2': 15, 'CCS': 11, 'CHAdeMO': 3 },
    oem_breakdown: { 'BENY': 12, 'ABB': 7, 'Alfen': 5, 'Wallbox': 4 },
    locations_by_org: [
      { name: 'SRLK EV Charge Ltd', count: 5 },
      { name: 'Greenway Motors', count: 3 },
      { name: 'Aberdeen Council', count: 2 },
    ],
    energy_by_org: [
      { name: 'SRLK EV Charge Ltd', energy_kwh: 1420 },
      { name: 'Greenway Motors', energy_kwh: 860 },
      { name: 'Aberdeen Council', energy_kwh: 410 },
    ],
    growth_chart,
    tariff_usage: [
      { name: 'Standard Rate', count: 6 },
      { name: 'Night Rate', count: 4 },
      { name: 'Fleet Discount', count: 2 },
    ],
    card_billing: { 'Free Charging': 3, 'Assigned Tariff': 9, 'Station Default': 14 },
    session_status_breakdown: { Completed: 142, Active: 7, Interrupted: 5 },
    payment_status_breakdown: { Captured: 118, Held: 7, Failed: 3 },
  };
}

const STATUS_COLORS = { Available: '#22c55e', Charging: '#1f9a4c', Offline: '#94a3b8', Faulted: '#ef4444' };
const PALETTE = ['#1f9a4c', '#2a8fd4', '#f59e0b', '#8b5cf6', '#ef4444', '#06b6d4'];
const SESSION_STATUS_COLORS = { Completed: '#1f9a4c', Active: '#2a8fd4', Pending: '#f59e0b', Interrupted: '#ef4444' };
const PAYMENT_COLORS = { Held: '#f59e0b', Captured: '#1f9a4c', Failed: '#ef4444' };
const CARD_BILLING_COLORS = { 'Free Charging': '#2a8fd4', 'Assigned Tariff': '#1f9a4c', 'Station Default': '#94a3b8' };

const IconChartBar = () => (
  <svg width="36" height="36" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
    <line x1="12" y1="20" x2="12" y2="10" /><line x1="18" y1="20" x2="18" y2="4" /><line x1="6" y1="20" x2="6" y2="16" />
  </svg>
);


function TrendIndicator({ value }) {
  if (value === null || value === undefined) return null;
  const isUp = value >= 0;
  return (
    <div className={`dash-stat-trend ${isUp ? 'up' : 'down'}`}>
      {isUp ? '▲' : '▼'} {Math.abs(value)}%
      <span style={{ color: 'var(--text-muted)', fontWeight: 400, marginLeft: 3 }}>vs previous period</span>
    </div>
  );
}

function DashEmptyChart({ message, height = 180 }) {
  return (
    <div className="dash-empty-chart" style={{ height }}>
      <IconChartBar />
      <p>{message}</p>
    </div>
  );
}

function DonutPanel({ title, dataObj, colorMap, height = 180 }) {
  const chartData = Object.entries(dataObj).map(([name, value]) => ({ name, value })).filter(d => d.value > 0);
  const total = chartData.reduce((sum, d) => sum + d.value, 0);

  return (
    <div className="dash-panel">
      <div className="dash-panel-header"><span className="dash-panel-title">{title}</span></div>
      {chartData.length === 0 ? (
        <DashEmptyChart message="No data yet." height={height} />
      ) : (
        <div className="dash-donut-wrap">
          <ResponsiveContainer width={130} height={130}>
            <PieChart>
              <Pie data={chartData} cx="50%" cy="50%" innerRadius={42} outerRadius={62} paddingAngle={chartData.length > 1 ? 3 : 0} dataKey="value">
                {chartData.map((entry, i) => (
                  <Cell key={i} fill={(colorMap && colorMap[entry.name]) || PALETTE[i % PALETTE.length]} />
                ))}
              </Pie>
              <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e2e8f0' }} />
            </PieChart>
          </ResponsiveContainer>
          <div className="dash-donut-legend">
            {chartData.map((d, i) => (
              <div className="dash-donut-legend-row" key={d.name}>
                <span className="dash-donut-legend-left">
                  <span className="dash-donut-legend-dot" style={{ background: (colorMap && colorMap[d.name]) || PALETTE[i % PALETTE.length] }} />
                  {d.name}
                </span>
                <span className="dash-donut-legend-value">{d.value}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function HorizontalBarPanel({ title, data, dataKey, labelKey, height = 180, color = '#1f9a4c', suffix = '' }) {
  return (
    <div className="dash-panel">
      <div className="dash-panel-header"><span className="dash-panel-title">{title}</span></div>
      {!data || data.length === 0 ? (
        <DashEmptyChart message="No data yet." height={height} />
      ) : (
        <ResponsiveContainer width="100%" height={height}>
          <BarChart data={data} layout="vertical" margin={{ top: 0, right: 20, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" horizontal={false} />
            <XAxis type="number" tick={{ fontSize: 11, fill: '#94a3b8' }} allowDecimals={false} />
            <YAxis type="category" dataKey={labelKey} tick={{ fontSize: 11.5, fill: '#475569' }} width={100} />
            <Tooltip
              contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e2e8f0' }}
              formatter={(value) => [`${value}${suffix}`, '']}
            />
            <Bar dataKey={dataKey} fill={color} radius={[0, 4, 4, 0]} barSize={16} />
          </BarChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}

export default function DashboardOverview() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState(30);
  const [demoMode, setDemoMode] = useState(false);

  const fetchData = async () => {
    try {
      const res = await axios.get(`${API}/dashboard/overview`, { params: { days: range } });
      setData(res.data);
    } catch (e) {
      console.error('Failed to fetch dashboard overview', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [range]);

  const displayData = demoMode ? generateDemoData(range) : data;

  if (loading) return <div className="loading">Loading dashboard...</div>;
  if (!displayData) return <div className="loading">No data available</div>;

  const {
    metrics, status_breakdown, sessions_chart, avg_duration_chart, connector_types,
    oem_breakdown, locations_by_org, energy_by_org, growth_chart, tariff_usage,
    card_billing, session_status_breakdown, payment_status_breakdown
  } = displayData;

  const hasSessions = metrics.total_sessions > 0;
  const oemChartData = Object.entries(oem_breakdown).map(([name, count]) => ({ name, count }));
  const gaugeData = [{ name: 'Active', value: metrics.total_chargers > 0 ? Math.round((metrics.active_chargers / metrics.total_chargers) * 100) : 0, fill: '#1f9a4c' }];

  return (
    <div className="dash-page">
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 16 }}>
        <div>
          <h1>Insights</h1>
          <p>Network-wide performance at a glance</p>
        </div>
        <button
          className={`btn ${demoMode ? 'btn-green' : 'btn-ghost'}`}
          onClick={() => setDemoMode(prev => !prev)}
        >
          {demoMode ? '✓ Preview Mode (Sample Data)' : 'Preview with Sample Data'}
        </button>
      </div>

      {demoMode && (
        <div className="schedule-gap-banner" style={{ background: '#eff6ff', borderColor: '#bfdbfe', marginBottom: 16 }}>
          <div className="schedule-gap-banner-title" style={{ color: '#1d4ed8' }}>
            ℹ You're viewing sample data to preview how this dashboard will look once real activity builds up. Toggle off to see your live network data.
          </div>
        </div>
      )}

      {/* ── Hero stats + trend, grouped together ── */}
      <div className="dash-panel" style={{ marginBottom: 16 }}>
        <div className="dash-hero-stats" style={{ marginBottom: 20 }}>
          <div className="dash-stat-card hero">
            <div className="dash-stat-label">Total Chargers</div>
            <div className="dash-stat-value">{metrics.total_chargers}</div>
          </div>
          <div className="dash-stat-card">
            <div className="dash-stat-label">Network Availability</div>
            <div className="dash-stat-value">{metrics.availability_percent}<span className="unit">%</span></div>
          </div>
          <div className="dash-stat-card">
            <div className="dash-stat-label">Sessions ({range}d)</div>
            <div className="dash-stat-value">{metrics.total_sessions}</div>
            <TrendIndicator value={displayData.trends?.total_sessions_change} />
          </div>
          <div className="dash-stat-card">
            <div className="dash-stat-label">Energy Delivered ({range}d)</div>
            <div className="dash-stat-value">{metrics.total_energy_kwh}<span className="unit">kWh</span></div>
            <TrendIndicator value={displayData.trends?.total_energy_kwh_change} />
          </div>
        </div>

        <div className="dash-panel-header">
          <span className="dash-panel-title">Sessions & Energy Trend</span>
          <div className="dash-range-pills">
            {[7, 30, 90].map(d => (
              <button key={d} className={`dash-range-pill ${range === d ? 'active' : ''}`} onClick={() => setRange(d)}>
                {d} days
              </button>
            ))}
          </div>
        </div>

        {!hasSessions ? (
          <DashEmptyChart message="No charging sessions yet — this chart will populate as activity begins." height={260} />
        ) : (
          <ResponsiveContainer width="100%" height={280}>
            <ComposedChart data={sessions_chart} margin={{ top: 0, right: 10, left: -20, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#94a3b8' }} interval={Math.floor(sessions_chart.length / 8)} />
              <YAxis yAxisId="left" tick={{ fontSize: 11, fill: '#94a3b8' }} allowDecimals={false} />
              <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11, fill: '#94a3b8' }} />
              <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e2e8f0' }} />
              <Legend
                verticalAlign="top"
                align="right"
                height={32}
                iconType="circle"
                iconSize={9}
                formatter={(value) => <span style={{ fontSize: 12.5, color: '#475569' }}>{value}</span>}
              />
              <Bar yAxisId="left" dataKey="sessions" fill="#2a8fd4" radius={[4, 4, 0, 0]} name="Sessions" />
              <Line yAxisId="right" type="monotone" dataKey="energy_kwh" stroke="#f59e0b" strokeWidth={2.5} dot={false} name="Energy (kWh)" />
            </ComposedChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* ── Row 1 ── */}
      <div className="dash-grid-3" style={{ marginBottom: 16 }}>
        <DonutPanel title="Charger Status" dataObj={status_breakdown} colorMap={STATUS_COLORS} />
        <DonutPanel title="Connector Types" dataObj={connector_types} />
        <HorizontalBarPanel title="Chargers by Manufacturer" data={oemChartData} dataKey="count" labelKey="name" color="#2a8fd4" />
      </div>

      {/* ── Row 2 ── */}
      <div className="dash-grid-3" style={{ marginBottom: 16 }}>
        <div className="dash-panel">
          <div className="dash-panel-header"><span className="dash-panel-title">Network Growth</span></div>
          {growth_chart.length === 0 ? (
            <DashEmptyChart message="No stations registered yet." />
          ) : (
            <ResponsiveContainer width="100%" height={180}>
              <AreaChart data={growth_chart} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                <defs>
                  <linearGradient id="growthGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#1f9a4c" stopOpacity={0.3} />
                    <stop offset="100%" stopColor="#1f9a4c" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#94a3b8' }} />
                <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} allowDecimals={false} />
                <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e2e8f0' }} />
                <Area type="monotone" dataKey="stations" stroke="#1f9a4c" strokeWidth={2} fill="url(#growthGradient)" />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className="dash-panel">
          <div className="dash-panel-header"><span className="dash-panel-title">Avg Session Duration</span></div>
          {!hasSessions ? (
            <DashEmptyChart message="No sessions yet." />
          ) : (
            <ResponsiveContainer width="100%" height={180}>
              <ComposedChart data={avg_duration_chart} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#94a3b8' }} interval={Math.floor(avg_duration_chart.length / 6)} />
                <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} />
                <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e2e8f0' }} formatter={(v) => [`${v} min`, 'Avg Duration']} />
                <Line type="monotone" dataKey="avg_minutes" stroke="#2a8fd4" strokeWidth={2.5} dot={{ r: 3 }} />
              </ComposedChart>
            </ResponsiveContainer>
          )}
        </div>

        <HorizontalBarPanel title="Energy by Organisation" data={energy_by_org} dataKey="energy_kwh" labelKey="name" color="#1f9a4c" suffix=" kWh" />
      </div>

      {/* ── Row 3 ── */}
      <div className="dash-grid-3" style={{ marginBottom: 16 }}>
        <DonutPanel title="Session Status" dataObj={session_status_breakdown} colorMap={SESSION_STATUS_COLORS} />
        <DonutPanel title="RFID Card Billing" dataObj={card_billing} colorMap={CARD_BILLING_COLORS} />
        <HorizontalBarPanel title="Tariff Usage (by station count)" data={tariff_usage} dataKey="count" labelKey="name" color="#f59e0b" />
      </div>

      {/* ── Row 4 ── */}
      <div className="dash-grid-3">
        <DonutPanel title="Payment Status" dataObj={payment_status_breakdown} colorMap={PAYMENT_COLORS} />
        <HorizontalBarPanel title="Locations by Organisation" data={locations_by_org} dataKey="count" labelKey="name" color="#8b5cf6" />

        <div className="dash-panel">
          <div className="dash-panel-header"><span className="dash-panel-title">Charger Utilisation</span></div>
          {metrics.total_chargers === 0 ? (
            <DashEmptyChart message="No chargers registered yet." />
          ) : (
            <div style={{ position: 'relative' }}>
              <ResponsiveContainer width="100%" height={180}>
                <RadialBarChart
                  cx="50%" cy="50%" innerRadius="65%" outerRadius="100%"
                  data={gaugeData} startAngle={90} endAngle={-270}
                >
                  <PolarAngleAxis type="number" domain={[0, 100]} angleAxisId={0} tick={false} />
                  <RadialBar background={{ fill: '#f1f5f9' }} dataKey="value" cornerRadius={10} />
                </RadialBarChart>
              </ResponsiveContainer>
              <div style={{
                position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
                textAlign: 'center'
              }}>
                <div style={{ fontSize: 26, fontWeight: 700, color: 'var(--gray-800)' }}>{gaugeData[0].value}%</div>
                <div style={{ fontSize: 11.5, color: 'var(--text-muted)' }}>{metrics.active_chargers} of {metrics.total_chargers} online</div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}